/**
 * VisualsView — Parsed Feedback view for runs whose prompt declares
 * `outputSchemaType: 'VisualsV1'` (i.e. `extract_visuals`). The parsed
 * JSON lives on `ModelRun.extractedData` (typed as ExtractedStepsData
 * historically; the runtime payload for visuals is a Record-shaped
 * object that follows the `extractVisualsSchema` shape).
 *
 * Schema-agnostic by design: admins can override the canonical schema
 * per-prompt, so this view reads from a loose `Record<string, unknown>`
 * and renders the canonical fields (`extracted_segments`, `relationships`,
 * work-quality) when present, gracefully handling extras and absences.
 */
import { useState } from 'react';
import { LatexText } from '@shared-ui/latex';
import { BboxOverlayModal } from './BboxOverlayModal';
import { ConfidenceBadge, confidenceClass } from './confidenceTint';
import { StudentWorkImages } from '../components';

interface VisualSegment {
    segment_id?: string;
    segment_type?: string;
    bbox?: number[];
    reading_order?: number;
    crossed_out?: boolean;
    literal_text?: string;
    semantic_description?: string;
    structured_data?: Record<string, unknown>;
    confidence?: number;
}

interface VisualRelationship {
    from_segment_id?: string;
    to_segment_id?: string;
    type?: string;
}

/** One image of the student's work exactly as this run sent it. */
export interface SentStudentImage {
    url: string;
    label?: string;
}

interface Props {
    data: Record<string, unknown>;
    /** The student images THIS run sent the model, in order — never an image
     *  the run did not send. One: the boxes are drawn on it. Several pages:
     *  the pages are shown as sent, without boxes, because a box does not say
     *  which page it is on. */
    sentImages?: SentStudentImage[];
}

function isSegment(x: unknown): x is VisualSegment {
    return !!x && typeof x === 'object' && !Array.isArray(x);
}

function isRelationship(x: unknown): x is VisualRelationship {
    return !!x && typeof x === 'object' && !Array.isArray(x);
}

export function VisualsView({ data, sentImages = [] }: Props) {
    const [showOverlay, setShowOverlay] = useState(false);
    const workQuality = data.work_quality as string | undefined;
    const workQualityConfidence = data.work_quality_confidence as number | undefined;
    const segments = Array.isArray(data.extracted_segments)
        ? (data.extracted_segments as unknown[]).filter(isSegment)
        : [];
    const relationships = Array.isArray(data.relationships)
        ? (data.relationships as unknown[]).filter(isRelationship)
        : [];

    // Entry point shows only when we have an image to draw on AND at least one
    // segment actually carries a bbox to draw.
    const hasDrawableBboxes = segments.some(
        s => Array.isArray(s.bbox) && s.bbox.length === 4,
    );
    const solutionImageUrl = sentImages.length === 1 ? sentImages[0].url : undefined;
    const canOverlay = !!solutionImageUrl && hasDrawableBboxes;
    const sentPages = sentImages.length > 1 ? sentImages : [];

    return (
        <div className="feedback-content">
            <div className="feedback-sections" style={{ marginBottom: 12 }}>
                <div className="feedback-section">
                    <h4>Work Quality</h4>
                    <p>
                        {workQuality ?? '-'}
                        {' '}
                        <ConfidenceBadge value={workQualityConfidence} />
                    </p>
                </div>
                <div className="feedback-section">
                    <h4>Segments</h4>
                    <p>{segments.length}</p>
                </div>
                <div className="feedback-section">
                    <h4>Relationships</h4>
                    <p>{relationships.length}</p>
                </div>
            </div>

            {segments.length > 0 ? (
                <div className="sa-steps-analysis">
                    <div className="visual-segments-head">
                        <h4 className="sa-title">Extracted Segments</h4>
                        {canOverlay && (
                            <button
                                type="button"
                                className="bbox-overlay-trigger"
                                onClick={() => setShowOverlay(true)}
                            >
                                View bboxes on image ↗
                            </button>
                        )}
                    </div>
                    <div className="sa-steps-list">
                        {segments.map((s, i) => (
                            <div
                                key={s.segment_id ?? i}
                                className={`sa-step-item ${s.crossed_out ? 'incorrect' : 'correct'} ${confidenceClass(s.confidence)}`}
                            >
                                <div className="sa-step-rail">
                                    {s.segment_id ?? `s${i + 1}`}
                                    {s.segment_type && (
                                        <span className="visual-segment-type">{s.segment_type}</span>
                                    )}
                                </div>
                                <div className="sa-step-body">
                                    {s.literal_text && (
                                        <div className="sa-step-content">
                                            <LatexText text={s.literal_text} />
                                        </div>
                                    )}
                                    {s.semantic_description && (
                                        <div className="visual-segment-description">
                                            <LatexText text={s.semantic_description} />
                                        </div>
                                    )}
                                    {s.structured_data && Object.keys(s.structured_data).length > 0 && (
                                        <details className="visual-segment-structured">
                                            <summary>structured_data</summary>
                                            <pre>{JSON.stringify(s.structured_data, null, 2)}</pre>
                                        </details>
                                    )}
                                    <div className="extracted-step-meta">
                                        {typeof s.reading_order === 'number' && <span>order: {s.reading_order}</span>}
                                        <ConfidenceBadge value={s.confidence} />
                                        {s.crossed_out && <span className="crossed-out">crossed out</span>}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <p className="empty">No segments extracted</p>
            )}

            {relationships.length > 0 && (
                <div className="visual-relationships">
                    <h4>Relationships</h4>
                    <ul>
                        {relationships.map((r, i) => (
                            <li key={i}>
                                <code>{r.from_segment_id ?? '?'}</code>
                                {' → '}
                                <code>{r.to_segment_id ?? '?'}</code>
                                {r.type && <span className="visual-relationship-type">{r.type}</span>}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {sentPages.length > 0 && (
                <div className="feedback-section">
                    <h4>Pages sent to the model</h4>
                    <p className="empty">
                        Boxes are not drawn: the reader read {sentPages.length} pages but does not say which page each box is on.
                    </p>
                    <StudentWorkImages pageUrls={sentPages.map(p => p.url)} alt="Page sent to the model" />
                </div>
            )}

            {showOverlay && solutionImageUrl && (
                <BboxOverlayModal
                    imageUrl={solutionImageUrl}
                    segments={segments}
                    onClose={() => setShowOverlay(false)}
                />
            )}
        </div>
    );
}
